/** IA da plataforma (T-40, HU-1101 a 1113): recomendações, configurações, "Perguntar à IA" e evidências. */
import { ApiError, toProblem, type HiveClient } from '@hive/api';
import type { HiveAnswer } from './answers';

export const REC_KINDS = ['security', 'patch', 'license-move', 'renewal', 'k8s-rightsizing'] as const;
export const AI_KINDS = ['security', 'patch', 'license-move', 'renewal', 'k8s-rightsizing', 'ask', 'help'] as const;
export const REC_STATES = ['new', 'review', 'approved', 'rejected', 'discarded-by-limit', 'not-generated', 'deadline-missed', 'done'] as const;
export const REJECT_REASONS = ['already-handled', 'wrong-number', 'business-constraint', 'other'] as const;
export const WEIGHTS = ['savings', 'performance', 'risk'] as const;
export const NEVER_REDUCE = ['antivirus', 'edr', 'backup', 'monitoring'] as const;
/** Só o "anthropic" tem conector nesta versão; os outros aparecem como "sem conector". */
export const HOSTING_WITH_CONNECTOR = ['anthropic', 'openai', 'azure-openai', 'gemini', 'openai-compatible'];
export const CPU_P95 = { min: 30, max: 95 } as const;

export type Tone = 'crit' | 'warn' | 'ok' | 'info' | 'neutral';

export function recStateTone(state?: string): Tone {
  switch (state) {
    case 'new':
      return 'info';
    case 'review':
      return 'warn';
    case 'approved':
    case 'done':
      return 'ok';
    case 'rejected':
    case 'deadline-missed':
      return 'crit';
    default:
      return 'neutral';
  }
}

/** Pesos somam 100: ao mexer num, os outros dois se ajustam na proporção. */
export function rebalance(w: Record<string, number>, key: string, value: number): Record<string, number> {
  const v = Math.max(0, Math.min(100, Math.round(value)));
  const [a = 'performance', b = 'risk'] = WEIGHTS.filter((k) => k !== key);
  const rest = (w[a] ?? 0) + (w[b] ?? 0);
  const left = 100 - v;
  const first = rest <= 0 ? Math.floor(left / 2) : Math.round(((w[a] ?? 0) / rest) * left);
  return { [key]: v, [a]: first, [b]: left - first };
}

/** Texto com [E1], [S2]… em pedaços: texto puro e referências (para virar link). */
export function splitRefs(text: string): Array<{ text: string } | { ref: string }> {
  const out: Array<{ text: string } | { ref: string }> = [];
  const re = /\[([ES]\d+)\]/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push({ text: text.slice(last, m.index) });
    out.push({ ref: m[1] ?? '' });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

/** As evidências [E1] citadas no texto, na ordem da primeira citação. */
export function citedRefs(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(/\[(E\d+)\]/g)) if (m[1] && !out.includes(m[1])) out.push(m[1]);
  return out;
}

/** Evidências citadas primeiro (na ordem da resposta) e as demais à parte. */
export function splitEvidence<T extends { id: string }>(evidence: T[], answer: string): { cited: T[]; others: T[] } {
  const refs = citedRefs(answer);
  const cited = refs.map((r) => evidence.find((e) => e.id === r)).filter((e): e is T => !!e);
  return { cited, others: evidence.filter((e) => !refs.includes(e.id)) };
}

/**
 * O pedaço do texto vem do núcleo como string JSON: o leitor de SSE tira o espaço depois de "data:",
 * e sem isso " máquinas" chegava "máquinas" (palavras grudadas). Núcleo antigo (texto cru) passa como veio.
 */
export function decodeDelta(data: string): string {
  if (!data.startsWith('"')) return data;
  const v = safeJson<unknown>(data, null);
  return typeof v === 'string' ? v : data;
}

export interface SseEvent {
  event: string;
  data: string;
}

/** Lê os quadros de um fluxo text/event-stream ("event: x\ndata: y\n\n"); devolve o que sobrou incompleto. */
export function parseSse(buffer: string): { events: SseEvent[]; rest: string } {
  const events: SseEvent[] = [];
  const norm = buffer.replace(/\r\n/g, '\n');
  const parts = norm.split('\n\n');
  const rest = parts.pop() ?? '';
  for (const block of parts) {
    let event = 'message';
    const data: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
    }
    if (data.length || event !== 'message') events.push({ event, data: data.join('\n') });
  }
  return { events, rest };
}

export interface Evidence {
  id: string;
  label?: string;
  data?: unknown;
}

export interface AskHandlers {
  onEvidence?: (e: Evidence[]) => void;
  onDelta?: (text: string) => void;
  onPrefill?: (p: { wizard?: string; prefill?: Record<string, unknown>; label?: string }) => void;
  onError?: (message: string) => void;
  /** R01-14: o próprio Hive respondeu (sem modelo); vem com a resposta inteira. */
  onAnswer?: (a: HiveAnswer) => void;
  /** Quem respondeu: "hive" ou "model" (no evento done). */
  onSource?: (source: 'hive' | 'model') => void;
  /** R01-16: a execução do modelo (no evento done), para a avaliação do operador ficar ligada a ela. */
  onRun?: (runId: string) => void;
}

/** "Perguntar à IA" (HU-1109): POST /ai/ask como fluxo SSE com eventos evidence, delta, prefill, done e error. */
/** De onde a pergunta veio (O1): a tela (ativo, alerta), o turno ou a montagem de uma regra. */
export type AskContext = { kind?: 'screen' | 'shift' | 'rule'; path?: string; assetId?: string; alertId?: string; hours?: number };

export async function askAi(api: HiveClient, body: { question: string; recommendationId?: string; context?: AskContext }, h: AskHandlers, signal?: AbortSignal): Promise<void> {
  const { response } = await api.POST('/api/v1/ai/ask', { body, parseAs: 'stream', signal });
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    throw new ApiError(response.status, toProblem(response.status, payload));
  }
  const reader = response.body?.getReader();
  if (!reader) return;
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const { events, rest } = parseSse(buf);
    buf = rest;
    for (const e of events) {
      if (e.event === 'evidence') h.onEvidence?.(safeJson<Evidence[]>(e.data, []));
      else if (e.event === 'delta') h.onDelta?.(decodeDelta(e.data));
      else if (e.event === 'prefill') h.onPrefill?.(safeJson(e.data, {}));
      else if (e.event === 'error') h.onError?.(e.data);
      else if (e.event === 'answer') h.onAnswer?.(safeJson<HiveAnswer>(e.data, {} as HiveAnswer));
      else if (e.event === 'done') {
        const d = safeJson<{ source?: string; runId?: string }>(e.data, {});
        h.onSource?.(d.source === 'hive' ? 'hive' : 'model');
        if (d.runId) h.onRun?.(d.runId);
        return;
      }
    }
  }
}

export function safeJson<T>(s: string, fallback: T): T {
  try {
    return JSON.parse(s) as T;
  } catch {
    return fallback;
  }
}

/** Números da recomendação com rótulo legível (as chaves vêm do núcleo em camelCase). */
export function numberEntries(n?: Record<string, unknown>): Array<[string, number | string]> {
  return Object.entries(n ?? {}).filter((e): e is [string, number | string] => typeof e[1] === 'number' || typeof e[1] === 'string');
}
